/**
 * [DSS] Print — Hidden Menu + Barcode + Image Toggle
 *
 * 1. Hidden Menu: 7x Klick auf SKU → Floating Menu mit Druck-Optionen
 * 2. JsBarcode: EAN als Strichcode rendern (gerade Linien)
 * 3. Image Toggle: Produktbild ein-/ausblenden vor Druck
 */
import { $, $$ } from '../utils/helpers.js';

const SKU_CLICK_THRESHOLD = 3;
const SKU_CLICK_TIMEOUT = 3000;
const JSBARCODE_CDN = 'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js';

// =====================================================
// 1) HIDDEN MENU — 7x SKU Click
// =====================================================

function initHiddenMenu() {
  const skuEl = $('.product__sku');
  if (!skuEl) {
    return;
  }


  let clickCount = 0;
  let lastClickTime = 0;

  const menu = document.createElement('div');
  menu.className = 'dss-print-hidden-menu';
  menu.setAttribute('role', 'menu');
  menu.setAttribute('aria-label', 'Druck-Optionen');
  menu.innerHTML = `
    <div class="dss-print-hidden-menu__title">Datenblatt</div>
    <button class="dss-print-hidden-menu__btn" data-dss-print-action="with-image" role="menuitem">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
      Datenblatt mit Bild drucken
    </button>
    <button class="dss-print-hidden-menu__btn" data-dss-print-action="without-image" role="menuitem">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="9" x2="15" y2="15"/><line x1="15" y1="9" x2="9" y2="15"/></svg>
      Datenblatt ohne Bild drucken
    </button>
  `;
  document.body.appendChild(menu);

  function handleSkuClick() {
    const now = Date.now();
    if (now - lastClickTime > SKU_CLICK_TIMEOUT) clickCount = 0;
    lastClickTime = now;
    clickCount++;
    if (clickCount >= SKU_CLICK_THRESHOLD) {
      clickCount = 0;
      toggleMenu(menu);
    }
  }

  skuEl.style.cursor = 'default';
  skuEl.addEventListener('click', (e) => { e.stopPropagation(); handleSkuClick(); });
  skuEl.querySelectorAll('span').forEach((span) => {
    span.addEventListener('click', (e) => { e.stopPropagation(); handleSkuClick(); });
  });

  menu.querySelectorAll('[data-dss-print-action]').forEach((btn) => {
    btn.addEventListener('click', () => {
      hideMenu(menu);
      setImageVisible(btn.getAttribute('data-dss-print-action') === 'with-image');
      setTimeout(() => window.print(), 100);
    });
  });

  document.addEventListener('click', (e) => {
    if (menu.classList.contains('is-visible') && !menu.contains(e.target) && !skuEl.contains(e.target)) {
      hideMenu(menu);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && menu.classList.contains('is-visible')) hideMenu(menu);
  });
}

function toggleMenu(menu) {
  menu.classList.contains('is-visible') ? hideMenu(menu) : showMenu(menu);
}

function showMenu(menu) {
  menu.classList.add('is-visible');
}

function hideMenu(menu) {
  menu.classList.remove('is-visible');
}

// =====================================================
// 2) IMAGE TOGGLE
// =====================================================

function setImageVisible(visible) {
  const printSection = $('.dss-product-print');
  if (!printSection) return;

  const body = printSection.querySelector('.dss-product-print__body');

  if (visible) {
    printSection.classList.remove('dss-product-print__image-hidden');
    printSection.setAttribute('data-dss-print-show-image', 'true');
    if (body) body.classList.add('dss-product-print__body--with-image');
  } else {
    printSection.classList.add('dss-product-print__image-hidden');
    printSection.setAttribute('data-dss-print-show-image', 'false');
    if (body) body.classList.remove('dss-product-print__body--with-image');
  }
}

// =====================================================
// 3) JsBarcode — EAN rendern
// =====================================================

function loadJsBarcode() {
  return new Promise((resolve) => {
    if (window.JsBarcode) { resolve(); return; }
    const script = document.createElement('script');
    script.src = JSBARCODE_CDN;
    script.onload = resolve;
      script.onerror = () => { resolve(); };
    document.head.appendChild(script);
  });
}

async function renderBarcodes() {
  const barcodeEls = $$('[data-dss-print-barcode]');
  if (!barcodeEls.length) return;

  await loadJsBarcode();

  barcodeEls.forEach((el) => {
    const ean = el.getAttribute('data-dss-print-barcode');
    const svgEl = el.querySelector('svg[data-dss-print-barcode-svg]');
    if (!ean || !svgEl || !window.JsBarcode) return;

    try {
      window.JsBarcode(svgEl, ean, {
        format: 'EAN13',
        width: 1.5,
        height: 50,
        displayValue: false,
        margin: 0,
        background: 'transparent',
        lineColor: '#000000',
        flat: true,
      });
    } catch (err) {
      svgEl.style.display = 'none';
    }
  });
}

// =====================================================
// INIT
// =====================================================

export function initPrintDatasheet() {
  initHiddenMenu();
  renderBarcodes();

  const printSection = $('.dss-product-print');
  if (printSection) {
    setImageVisible(printSection.getAttribute('data-dss-print-show-image') === 'true');
  }
}
